import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { MyJlFilled } from 'jl-design-system/components/icons/MyJlFilled';
import { MobilePhoneOutlined } from 'jl-design-system/components/icons/MobilePhoneOutlined';
import { FeedbackOutlined } from 'jl-design-system/components/icons/FeedbackOutlined';
import styles from './MobileMenu.scss';
import { SubMenu } from './SubMenu';
import { filterMenuData } from '../../../utils/menu/filterMenuData';
import { brandsData, servicesData, helpData } from './tabbedMenuData';
import { FEEDBACK_LINK } from '../../links';

const InspirationLink = {
  name: 'Inspiration',
  url: '/content',
  id: '10139580012',
  includedInMenus: ['MOBILE_WEB'],
};

export const MobileMenu = ({
  menus: initialMenus,
  fetchChildCategories,
  typeaheadIsOpen = false,
  isVisible = true,
  onAnimationComplete = () => {},
}) => {
  const rootMenuName = initialMenus ? initialMenus[0]?.name : '';

  const initialMenusFiltered = filterMenuData(initialMenus[0]?.child, [
    'MOBILE_WEB',
  ]);

  const initialRootMenuItems = [
    ...(initialMenusFiltered || []),
    InspirationLink,
  ];

  const findMenuItemByName = name => {
    return initialRootMenuItems.find(item => item.name === name);
  };

  const brandsMenuItems = findMenuItemByName('Brands');
  const servicesMenuItems = findMenuItemByName('Services');

  const [rootMenuItems, setRootMenuItems] = useState(initialRootMenuItems);

  const top = useRef();
  const [activeLevel, setActiveLevel] = useState(0);

  const scrollToTop = () => {
    if (top.current && top.current.scrollBy) {
      top.current.scrollBy(0, -2000);
    }
  };

  const [activeTab, setActiveTab] = useState('Shop');

  const [animationKey, setAnimationKey] = useState(0);
  const [isClosing, setIsClosing] = useState(false);
  const prevIsVisible = useRef(isVisible);

  useEffect(function remountNavAndForceOpenAnimation() {
    setAnimationKey(prev => prev + 1);
  }, []);

  // Handle closing animation
  useEffect(
    function handleCloseAnimation() {
      const shouldBeClosing = !isVisible && prevIsVisible.current === true;

      if (shouldBeClosing && !isClosing) {
        setIsClosing(true);
        const timer = setTimeout(() => {
          setIsClosing(false);
          onAnimationComplete();
        }, 500); // 300ms for animation + buffer
        return function clearCloseTimer() {
          clearTimeout(timer);
        };
      } else prevIsVisible.current = isVisible;
    },
    [isVisible, onAnimationComplete]
  );

  // Handle reopening while closing
  useEffect(
    function cancelCloseOnReopen() {
      if (isVisible && isClosing) {
        setIsClosing(false);
      }
    },
    [isVisible, isClosing]
  );

  // The Money menu items need to be displayed in their own tab
  // so we need to extract them from the Services menu items
  // and create a new menu for them
  const moneyMenuName = 'John Lewis Money';

  const findMoneyMenu = (menuItems, fallbackData) => {
    const source = menuItems || fallbackData;
    return {
      child: [source.child.find(item => item.name === moneyMenuName)].filter(
        Boolean
      ),
    };
  };

  const filterMoneyMenu = (menuItems, fallbackData) => {
    const source = menuItems || fallbackData;
    return {
      child: source.child.filter(item => item.name !== moneyMenuName),
    };
  };

  const servicesMenuContent = filterMoneyMenu(
    servicesMenuItems,
    servicesData
  ).child;

  const johnLewisMoneyData = findMoneyMenu(servicesMenuItems, servicesData);

  useEffect(
    function syncRootMenuWithActiveTab() {
      let updatedSubMenuData = null;
      if (activeTab === 'Brands') {
        if (brandsMenuItems) {
          updatedSubMenuData = brandsMenuItems?.child;
        } else {
          updatedSubMenuData = brandsData?.child;
        }
      }
      if (activeTab === 'Services') {
        updatedSubMenuData = servicesMenuContent;
      }
      if (activeTab === 'Help') {
        updatedSubMenuData = helpData?.child;
      }
      if (activeTab === 'Money') {
        updatedSubMenuData = johnLewisMoneyData?.child;
      }
      if (activeTab !== 'Shop') {
        setRootMenuItems(updatedSubMenuData);
      } else {
        setRootMenuItems(
          initialRootMenuItems.filter(
            item => item.name !== 'Brands' && item.name !== 'Services'
          )
        );
      }
    },
    [activeTab]
  );

  const mounted = useRef(false);
  useEffect(function trackMountedState() {
    mounted.current = true;
    return function markAsUnmounted() {
      mounted.current = false;
    };
  }, []);

  const fetchSubMenuData = async (menuItemId, menuIndex) => {
    if (rootMenuItems[menuIndex].isCached) {
      return;
    }

    try {
      const fetchedData = await fetchChildCategories({
        id: menuItemId,
      });

      if (mounted.current === false) {
        return;
      }

      if (!fetchedData || fetchedData.error) {
        throw new Error('Fetch failed');
      }

      setRootMenuItems(existingRootMenuItems =>
        existingRootMenuItems.map((existing, i) =>
          i === menuIndex
            ? {
                ...existing,
                child: fetchedData.child || [],
                isCached: true,
              }
            : existing
        )
      );
      // eslint-disable-next-line no-unused-vars
    } catch (e) {
      console.error(`fetch failed for category id: ${menuItemId}`);
    }
  };

  const activeMenuStyles = [
    styles.activeLevelRoot,
    styles.activeLevelFirst,
    styles.activeLevelSecond,
    styles.activeLevelThird,
  ];
  const subMenuClassName = classNames([
    styles.subMenu,
    activeMenuStyles[activeLevel],
  ]);

  const shouldBeClosing = !isVisible && prevIsVisible.current === true;
  const shouldRender = isVisible || isClosing || shouldBeClosing;

  if (!shouldRender) {
    return null;
  }

  return (
    <>
      <nav
        className={classNames(styles.mobileMenu, {
          [styles.mobileMenu__typeaheadOpen]: typeaheadIsOpen,
          [styles['mobileMenu--open-animated']]: isVisible && !isClosing,
          [styles['mobileMenu--close-animated']]: isClosing,
          [styles['mobileMenu--white-bg']]: activeTab === 'Shop',
        })}
        key={animationKey}
        ref={top}
        data-testid="mobile-navigation-menu"
      >
        <div
          className={subMenuClassName}
          data-testid="mobile-navigation-container"
        >
          <SubMenu
            level={0}
            setActiveLevel={setActiveLevel}
            setActiveTab={setActiveTab}
            activeTab={activeTab}
            subMenuData={{
              name: rootMenuName,
              child: rootMenuItems,
              backLabel: undefined,
            }}
            onItemSelected={(menuItemId, menuIndex) => {
              fetchSubMenuData(menuItemId, menuIndex);
            }}
            scrollToTop={scrollToTop}
          >
            <div
              data-testid="mobile-menu-static-section"
              className={classNames(
                styles.staticSection,
                styles.feedbackMenuBottom
              )}
            >
              <div className={styles.footer}>
                <ul>
                  <li className={styles.footer__link}>
                    <MyJlFilled className={styles.invertedIcon} size="medium" />
                    <a href="/our-services/my-john-lewis">My John Lewis</a>
                  </li>
                  <li className={styles.footer__link}>
                    <MobilePhoneOutlined
                      className={styles.invertedIcon}
                      size="medium"
                    />
                    <a href="/customer-services/shopping-with-us/our-apps">
                      Download the John Lewis app
                    </a>
                  </li>
                  <li className={styles.footer__link}>
                    <FeedbackOutlined
                      className={styles.invertedIcon}
                      size="medium"
                    />
                    <a
                      href={FEEDBACK_LINK}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Leave feedback
                    </a>
                  </li>
                  <li
                    className={classNames(
                      styles.footer__link,
                      styles.footer__nku
                    )}
                  >
                    <a href="/content/never-knowingly-undersold">
                      Never Knowingly Undersold
                    </a>
                  </li>
                </ul>
              </div>
            </div>
          </SubMenu>
        </div>
      </nav>
    </>
  );
};

const itemShape = {
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  url: PropTypes.string,
};
itemShape.child = PropTypes.arrayOf(PropTypes.shape(itemShape), null);

MobileMenu.propTypes = {
  menus: PropTypes.arrayOf(PropTypes.shape(itemShape)).isRequired,
  fetchChildCategories: PropTypes.func.isRequired,
  typeaheadIsOpen: PropTypes.bool,
  isVisible: PropTypes.bool,
  onAnimationComplete: PropTypes.func,
};
